// ============================================================================
// THE "TODAY AT BERKELEY" TRACK — where a class sits on the card's little timeline, in
// FRACTIONS of the track rather than pixels.
//
// WHY THIS FILE EXISTS AT ALL (owner bug report, 2026-08-28 — a 17:00 class drawn on the 15:00
// gridline). The card used to compute `top` in pixels against a `TRACK_PX = 172` constant while
// the track element was `min-h-[172px] flex-1` — so the moment the card grew taller than 172px,
// the axis labels (a `justify-between` column, which stretches) and the blocks (pinned to a
// constant that does not) were measuring two different rulers. Every block landed
// proportionally too high, and the higher the card, the bigger the lie.
//
// THE RULE THIS FILE ENFORCES: nothing on the track is positioned in pixels. Labels, gridlines,
// blocks and the now-line all come out of `trackFraction` and are rendered as percentages, so
// the geometry is correct at any rendered height and cannot silently drift again. Block HEIGHTS
// are fractions of the same track for the same reason — a percentage top with a pixel height
// would be the identical bug in a new costume the first time the stacking push had to compare
// them.
//
// It is a separate module because a fraction is testable and a `style={{ top }}` is not.
// ============================================================================

/**
 * The window the card draws. 09:00–19:00, not the mockup's 09:00–18:00: NEU C61 runs 17:00–18:29
 * and the old ceiling cut its block off at the bottom edge (owner report, same day).
 *
 * These are a FLOOR and a CEILING, not the whole story — `trackWindow` widens them to whatever
 * the day actually holds, because a card that clamps an 08:00 lab onto the 09:00 line is back to
 * drawing a class at a time it does not happen.
 */
export const DEFAULT_DAY_FROM = 9 * 60
export const DEFAULT_DAY_TO = 19 * 60

/**
 * The track's `min-h`, and the two block heights the handoff draws against it.
 *
 * A BLOCK KEEPS ITS DESIGNED PIXEL HEIGHT — 52px for the imminent class, 34px for the rest — and
 * the layout reasons about it as a fraction OF THE FLOOR. On a track taller than the floor the
 * rendered block is proportionally shorter than that allowance, never taller, so every "does it
 * fit" and "does it clear the one above" answer computed here is conservative at any real height.
 * That is the whole trick: the POSITION scales with the element, the SIZE does not, and the two
 * can only ever err in the safe direction.
 */
export const TRACK_FLOOR_PX = 172
export const NEXT_BLOCK_PX = 52
export const PLAIN_BLOCK_PX = 34

const NEXT_HEIGHT = NEXT_BLOCK_PX / TRACK_FLOOR_PX
const PLAIN_HEIGHT = PLAIN_BLOCK_PX / TRACK_FLOOR_PX
const STACK_GAP = 6 / TRACK_FLOOR_PX

export type TrackSpan = { startMinutes: number; endMinutes: number }

export type TrackWindow = { from: number; to: number }

/**
 * The drawn window: the default one, widened to whole hours around anything that falls outside
 * it. Nothing is ever clamped, so no block is ever drawn at a time it does not happen.
 */
export function trackWindow(rows: readonly TrackSpan[]): TrackWindow {
  let from = DEFAULT_DAY_FROM
  let to = DEFAULT_DAY_TO
  for (const r of rows) {
    from = Math.min(from, Math.floor(r.startMinutes / 60) * 60)
    to = Math.max(to, Math.ceil(r.endMinutes / 60) * 60)
  }

  // A BLOCK IS ANCHORED BY ITS TOP AND HAS A FIXED HEIGHT, so covering the last class's end hour
  // is not enough: the window also has to leave a block's worth of room BELOW the last start, or
  // the card draws a real class half off its own bottom edge. That is the other half of the
  // owner's 17:00 report — the old 09:00–18:00 window put the block's top at 89% of a track it
  // then needed 30% more of.
  for (const r of rows) {
    const needed = from + (r.startMinutes - from) / (1 - NEXT_HEIGHT)
    to = Math.max(to, Math.ceil(needed / 60) * 60)
  }

  // AND IT STOPS AT MIDNIGHT. The line above solves for room, not for a real hour, and a 22:00
  // seminar solves to 31:00 — an hour that does not exist and that the gutter would cheerfully
  // label "7 PM". Past this point the block is bottom-pinned by `layoutClassTrack` instead: a
  // couple of percent high, which is a far smaller lie than an invented hour on the axis.
  return { from, to: Math.min(to, 24 * 60) }
}

/** Where a minute sits on the track, 0 at the top edge and 1 at the bottom. */
export function trackFraction(minutes: number, { from, to }: TrackWindow): number {
  return (minutes - from) / (to - from)
}

/**
 * The four hour marks down the gutter, on whole hours, first and last always the window's own
 * edges. A 09:00–18:00 window gives back the mockup's 9/12/15/18 exactly; 09:00–19:00 gives
 * 9/13/17/19. They are POSITIONED by `trackFraction` like everything else, never spread with
 * `justify-between` — a label that finds its own place is a label that can disagree with a block.
 */
export function axisHours({ from, to }: TrackWindow): number[] {
  const spanHours = (to - from) / 60
  const step = Math.max(1, Math.ceil(spanHours / 3))
  const marks: number[] = []
  for (let m = from; m < to; m += step * 60) marks.push(m)
  marks.push(to)
  return marks
}

export type PlacedClass<T> = {
  row: T
  index: number
  isNext: boolean
  /** 0–1 down the track. Multiply by 100 for a CSS `top`. */
  topFraction: number
  /** The block's designed height in px — 52 for the imminent class, 34 for the rest. */
  heightPx: number
  /** The room the layout RESERVED for it, as a fraction of the floor. Never less than it takes. */
  heightFraction: number
}

/**
 * Position by time, then push each block down far enough that it cannot cover the one above —
 * the card's original rule, in fractions. The imminent class gets the taller block.
 */
export function layoutClassTrack<T extends TrackSpan>(
  rows: readonly T[],
  nowMinutes: number,
  window: TrackWindow,
): { placed: PlacedClass<T>[]; nextIndex: number; nowFraction: number | null } {
  const nextIndex = rows.findIndex((r) => r.endMinutes > nowMinutes)

  let cursor = -Infinity
  const placed = rows.map((row, index) => {
    const isNext = index === nextIndex
    const heightPx = isNext ? NEXT_BLOCK_PX : PLAIN_BLOCK_PX
    const heightFraction = isNext ? NEXT_HEIGHT : PLAIN_HEIGHT
    // The push can only move a block DOWN, and `trackWindow` guarantees an unpushed block fits;
    // the `min` is the belt for the pathological case of classes stacked on top of each other,
    // where the last one is already off its own time and being off the card too helps nobody.
    const topFraction = Math.min(
      Math.max(trackFraction(row.startMinutes, window), cursor),
      1 - heightFraction,
    )
    cursor = topFraction + heightFraction + STACK_GAP
    return { row, index, isNext, topFraction, heightPx, heightFraction }
  })

  const inside = nowMinutes >= window.from && nowMinutes <= window.to
  return { placed, nextIndex, nowFraction: inside ? trackFraction(nowMinutes, window) : null }
}
